import React from "react";
import parse from "html-react-parser";
import { Box, Typography, Button, ButtonProps, useMediaQuery, useTheme } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import clsx from "clsx";
import CheckIcon from "@mui/icons-material/Check";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoIcon from "@mui/icons-material/Info";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

import {
  MOBILE_CARD_PADDING,
  MOBILE_CARD_RADIUS,
  MOBILE_CTA_HEIGHT,
  MOBILE_FONT,
} from "@APP/styles/mobile";
import { DESKTOP_STATUS } from "@APP/styles/status";

interface MessageButton extends ButtonProps {
  text?: string;
}

export interface Props {
  type?: "success" | "warning" | "info" | "error";
  title?: string;
  description?: string;
  /**
   * Lloyds-only: renders arbitrary nodes inside the white description card
   * instead of the parsed `description` string. Used where the card holds
   * structured data (the payment-success details) rather than copy — unlike
   * `children`, it keeps the icon and title above the card.
   */
  descriptionContent?: React.ReactNode;
  buttons?: MessageButton[];
  showIcon?: boolean;
  showClockIcon?: boolean;
  // Lloyds-only: render a brand SVG (e.g. the red-circle error icon) instead
  // of the built-in MUI icon.
  iconSrc?: string;
  /**
   * Lloyds-only: overrides the fixed 128px default size for `iconSrc`. Used
   * to pass the mobile-specific sizes (`MOBILE_ICON`).
   */
  iconSize?: number;
  /**
   * Lloyds-only: desktop gap between the description card and the CTA row.
   * Defaults to the Figma's `DESKTOP_STATUS.ACTIONS_GAP`; pass 0 on the
   * screens whose design tucks the CTAs straight under the card. Mobile keeps
   * its own fixed gap regardless.
   */
  actionsGap?: number;
  style?: React.CSSProperties;
  children?: React.ReactNode | string;
}

interface StyleProps {
  type: Props["type"];
  iconSize?: number;
  actionsGap: number;
}

const useStyles = makeStyles((theme) => ({
  cardWarning: {
    color: theme.palette.text.primary,
  },
  cardError: {
    // The design keeps the error title black; the red is carried by the icon.
    color: theme.palette.text.primary,
  },
  cardSuccess: {
    color: theme.palette.text.primary,
  },
  cardInfo: {
    color: theme.palette.text.primary,
  },
  icon: {
    fontSize: "8.5vh",
    color: (props: StyleProps) => theme.palette[props.type!].main,
  },
  imgIcon: {
    width: (props: StyleProps) => props.iconSize ?? 128,
    height: (props: StyleProps) => props.iconSize ?? 128,
    objectFit: "contain",
  },
  // The LBG designs put the message body inside a full-width white card with
  // left aligned copy, with any action button right aligned underneath it.
  descriptionCard: {
    width: "100%",
    textAlign: "left",
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius * 1.6,
    padding: theme.spacing(3),
    "& ul": {
      margin: 0,
      paddingLeft: theme.spacing(3),
    },
    [theme.breakpoints.down("sm")]: {
      borderRadius: MOBILE_CARD_RADIUS,
      padding: MOBILE_CARD_PADDING,
      marginTop: 20,
      fontSize: MOBILE_FONT.body,
    },
  },
  buttonsRow: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(2),
    marginTop: (props: StyleProps) => props.actionsGap,
    [theme.breakpoints.down("sm")]: {
      // The mobile designs use a single full-width CTA.
      flexDirection: "column",
      alignItems: "stretch",
      gap: theme.spacing(2),
      marginTop: 16,
      "& .MuiButton-root": {
        minHeight: MOBILE_CTA_HEIGHT,
        width: "100%",
      },
    },
  },
}));

// `sx` rather than `makeStyles` for anything overriding a Typography variant:
// JSS and emotion classes have equal specificity, so the JSS rule can lose to
// the variant's own font size depending on stylesheet order.
const MOBILE_TITLE_SX = {
  fontSize: `${MOBILE_FONT.subHeadline}px`,
  lineHeight: 1.2,
  maxWidth: 300,
  marginInline: "auto",
};

const MOBILE_DESCRIPTION_SX = { fontSize: `${MOBILE_FONT.body}px` };

const Message = ({
  type = "info",
  title,
  description,
  descriptionContent,
  buttons,
  showIcon = true,
  showClockIcon = false,
  iconSrc,
  iconSize,
  actionsGap = DESKTOP_STATUS.ACTIONS_GAP,
  style,
  children,
}: Props) => {
  const classes = useStyles({ type, iconSize, actionsGap });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  return (
    <Box
      textAlign="center"
      width="100%"
      id={"messageBox"}
      className={clsx({
        [classes.cardSuccess]: type === "success",
        [classes.cardWarning]: type === "warning",
        [classes.cardInfo]: type === "info",
        [classes.cardError]: type === "error",
      })}
      style={style}>
      <Box
        display="flex"
        id={"messageBox2"}
        flexDirection="column"
        alignItems="center"
        py={isMobile ? 0 : 3}>
        {showIcon && (
          <Box mb={isMobile ? "16px" : "2.8vh"}>
            {iconSrc ? (
              <img src={iconSrc} className={classes.imgIcon} alt="" />
            ) : showClockIcon ? (
              <AccessTimeIcon className={classes.icon} />
            ) : (
              <>
                {type === "success" && <CheckIcon className={classes.icon} />}
                {type === "warning" && <WarningIcon className={classes.icon} />}
                {type === "info" && <InfoIcon className={classes.icon} />}
                {type === "error" && <ErrorOutlineIcon className={classes.icon} />}
              </>
            )}
          </Box>
        )}
        {!children && (
          <>
            {title && (
              <Box mb={isMobile ? "16px" : "2.8vh"}>
                {/* h4 rather than h6: the designs render these status titles at
                    the same prominent headline weight as the journey screens'
                    page titles (also h4), not MUI's much smaller default h6. */}
                <Typography
                  color="inherit"
                  variant="h4"
                  sx={isMobile ? MOBILE_TITLE_SX : { fontSize: "30px" }}
                  id={"messageTitle"}>
                  {title}
                </Typography>
              </Box>
            )}
            {descriptionContent ? (
              <Box mb={isMobile ? "16px" : "2.8vh"} className={classes.descriptionCard}>
                {descriptionContent}
              </Box>
            ) : (
              description && (
                <Box mb={isMobile ? "16px" : "2.8vh"} className={classes.descriptionCard}>
                  <Typography
                    color="textPrimary"
                    sx={isMobile ? MOBILE_DESCRIPTION_SX : { fontSize: "16px" }}
                    id={"messageDescription"}>
                    {parse(description)}
                  </Typography>
                </Box>
              )
            )}
          </>
        )}
        <Box>
          {typeof children === "string" ? (
            <Typography color="inherit">{children}</Typography>
          ) : (
            children
          )}
        </Box>
      </Box>
      {!!buttons?.length && (
        <Box className={classes.buttonsRow}>
          {buttons.map(
            ({ text = "Okay", variant = "text", color = "inherit", ...props }, index) => (
              <Button
                variant={variant}
                color={color}
                key={`${text}${index}`}
                {...props}
                id={"messageButton" + index}>
                {text}
              </Button>
            ),
          )}
        </Box>
      )}
    </Box>
  );
};

export default Message;
